import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  DatabaseOutlined,
  WalletOutlined,
  RiseOutlined,
  SwapOutlined,
  UserOutlined,
  TeamOutlined,
  BankOutlined,
  TableOutlined,
  LineChartOutlined,
  FileProtectOutlined,
  FileSyncOutlined,
  WarningOutlined,
  FallOutlined,
  OrderedListOutlined,
  CalculatorOutlined,
} from '@ant-design/icons';
import type { PanelScreen, ReportConfig } from '../types/panelConfig';
import { TierBadge } from './TierBadge';

const iconMap: Record<string, React.ReactNode> = {
  ShoppingCartOutlined: <ShoppingCartOutlined />,
  DatabaseOutlined: <DatabaseOutlined />,
  WalletOutlined: <WalletOutlined />,
  RiseOutlined: <RiseOutlined />,
  SwapOutlined: <SwapOutlined />,
  UserOutlined: <UserOutlined />,
  TeamOutlined: <TeamOutlined />,
  BankOutlined: <BankOutlined />,
  TableOutlined: <TableOutlined />,
  DashboardOutlined: <DashboardOutlined />,
  LineChartOutlined: <LineChartOutlined />,
  FileProtectOutlined: <FileProtectOutlined />,
  FileSyncOutlined: <FileSyncOutlined />,
  WarningOutlined: <WarningOutlined />,
  FallOutlined: <FallOutlined />,
  OrderedListOutlined: <OrderedListOutlined />,
  CalculatorOutlined: <CalculatorOutlined />,
};

interface DynamicSidebarProps {
  reports: ReportConfig[];
  screens?: PanelScreen[];
  collapsed?: boolean;
}

export function DynamicSidebar({ reports, screens = [], collapsed = false }: DynamicSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const categories = useMemo(() => {
    const m = new Map<string, ReportConfig[]>();
    for (const r of reports) {
      const list = m.get(r.category) || [];
      list.push(r);
      m.set(r.category, list);
    }
    return Array.from(m.entries());
  }, [reports]);

  return (
    <>
      <style>{sidebarCss}</style>

      <nav style={{ padding: '10px 0 20px', display: 'flex', flexDirection: 'column' }}>
        <Row
          active={location.pathname === '/'}
          onClick={() => navigate('/')}
          icon={<DashboardOutlined />}
          label="Özet"
          collapsed={collapsed}
        />

        {screens.map((s) => {
          const path = `/${s.key}`;
          return (
            <Row
              key={s.key}
              active={location.pathname === path}
              onClick={() => navigate(path)}
              icon={<LineChartOutlined />}
              label={s.title}
              collapsed={collapsed}
            />
          );
        })}

        {categories.map(([category, list]) => (
          <div key={category} style={{ marginTop: 10 }}>
            {!collapsed && <div className="rg-sidebar-cat">{category}</div>}
            {list.map((r) => {
              const path = `/rapor/${r.code}`;
              return (
                <Row
                  key={r.code}
                  active={location.pathname === path}
                  onClick={() => navigate(path)}
                  icon={iconMap[r.icon ?? ''] ?? <TableOutlined />}
                  label={r.name}
                  tier={r.tier}
                  collapsed={collapsed}
                />
              );
            })}
          </div>
        ))}
      </nav>
    </>
  );
}

interface RowProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  tier?: ReportConfig['tier'];
  collapsed: boolean;
}

function Row({ icon, label, active, onClick, tier, collapsed }: RowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rg-sidebar-row ${active ? 'active' : ''}`}
      style={{ justifyContent: collapsed ? 'center' : 'flex-start' }}
      title={collapsed ? label : undefined}
    >
      <span className="rg-sidebar-icon">{icon}</span>
      {!collapsed && (
        <>
          <span className="rg-sidebar-label">{label}</span>
          {tier && (
            <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
              <TierBadge tier={tier} />
            </span>
          )}
        </>
      )}
    </button>
  );
}

const sidebarCss = `
  .rg-sidebar-cat {
    padding: 10px 18px 4px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.9px;
    color: var(--color-text-muted);
    text-transform: uppercase;
  }
  .rg-sidebar-row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 7px 18px 7px 15px;
    border: none;
    border-left: 3px solid transparent;
    background: transparent;
    color: var(--color-text-secondary);
    font-size: 13px;
    font-weight: 400;
    cursor: pointer;
    text-align: left;
    transition: background .1s ease, color .1s ease, border-color .1s ease;
    min-height: 32px;
  }
  .rg-sidebar-row:hover {
    background: var(--color-hover-bg);
    color: var(--color-text-primary);
  }
  .rg-sidebar-row.active {
    background: var(--color-hover-bg);
    color: var(--color-text-primary);
    font-weight: 600;
    border-left-color: var(--color-primary);
  }
  .rg-sidebar-icon {
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    color: var(--color-text-muted);
    flex-shrink: 0;
  }
  .rg-sidebar-row.active .rg-sidebar-icon { color: var(--color-text-primary); }
  .rg-sidebar-label {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
